import React, { Fragment, useMemo } from "react";

import { Text, View, StyleSheet } from "@react-pdf/renderer";

import { ReportQuestionEntryProps } from "../ReportQuestion";
import {
  parseQuestion,
  parseAnswers,
} from "../../../reports/TemplateQuestionsImpl/TemplateQuestionSelectorSingle";
import { getReportAnswersByRole } from "../../../reports/TemplateQuestion";

import ReportQuestionTitle from "./utils/ReportQuestionTitle";
import ReportAnswerPDF from "./utils/ReportAnswer";
import Badge from "./utils/Badge";
import Radio from "./utils/Radio";

const styles = StyleSheet.create({
  answerGroupContainer: {
    paddingHorizontal: "4mm",
    paddingBottom: "3mm",
    marginVertical: "1.5mm",
  },
  answerGroupHeader: {
    fontSize: "3.7mm",
    color: "#2b3e54",
  },
  answerGroup: {
    paddingTop: "3mm",
  },
  optionsContainer: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    paddingBottom: "4mm",
  },
  spacer: {
    paddingLeft: "6mm",
  },
});

const ReportQuestionSelectorSingle: React.FC<ReportQuestionEntryProps> = ({
  mad: mad,
  question: rawQuestion,
  answers: rawAnswers,
  index,
  t,
}) => {
  const question = useMemo(() => parseQuestion(rawQuestion), [rawQuestion]);
  const answers = useMemo(
    () => getReportAnswersByRole(rawAnswers, parseAnswers, mad),
    [rawAnswers]
  );

  const timeSpent = useMemo(() => {
    let tSpent = 0;

    answers.referent.forEach((a) => (tSpent += a.timeSpent));
    return Math.floor(tSpent / answers.referent.length);
  }, [answers]);

  return (
    <View>
      <ReportQuestionTitle
        title={question.label}
        questionDescription={question.description}
        timeSpent={question.timeTracked ? timeSpent : undefined}
        description={t("reports.singleSelectorQuestion") as string}
        index={index + 1}
      />
      <View style={styles.optionsContainer}>
        {question.options.map((opt, index) => (
          <Fragment key={opt.label}>
            {index > 0 && <View style={styles.spacer} />}
            <Radio label={opt.label} />
          </Fragment>
        ))}
      </View>
      <View style={styles.answerGroupContainer}>
        <Text style={styles.answerGroupHeader}>
          {t("reports.talentAnswer")}
        </Text>
        <View style={styles.answerGroup}>
          {answers.talent.map((a) => (
            <ReportAnswerPDF
              answer={a}
              answerAs={
                <Badge
                  label={
                    question.options.find((o) => o.value === a.value.selector)
                      ?.label
                  }
                />
              }
              withComment
              comment={a.value.comment}
              key={a.id}
              t={t}
            />
          ))}
        </View>
      </View>
      <View style={styles.answerGroupContainer}>
        <Text style={styles.answerGroupHeader}>
          {t("reports.evaluatorsAnswers", { count: answers.referent.length })}
        </Text>
        <View style={styles.answerGroup}>
          {answers.referent.map((a) => (
            <ReportAnswerPDF
              answer={a}
              answerAs={
                <Badge
                  label={
                    question.options.find((o) => o.value === a.value.selector)
                      ?.label
                  }
                />
              }
              withComment
              comment={a.value.comment}
              key={a.id}
              t={t}
            />
          ))}
        </View>
      </View>
    </View>
  );
};

export default ReportQuestionSelectorSingle;
